/**
 * How archived things look: the Active/Archived tabs on a list (the tab is in
 * the address, `?view=archived`), the badge on history, the read-only banner
 * on an archived page, the empty states of both tabs, and a picker's "N
 * archived also match" hint.
 */

import type { ReactNode } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import { EmptyState } from "@/components/ui/states";
import { Tabs } from "@/components/ui/tabs";
import { formatDate, plural } from "@/lib/format";
import { useArchivedList, useArchiveView, type ArchiveView } from "./hooks";
import { KINDS, type Archivable, type ArchiveKind } from "./kinds";

export function ArchiveTabs({
  kind,
  active,
  archived,
}: {
  kind: ArchiveKind;
  active?: number | undefined;
  archived?: number | undefined;
}) {
  const [view, setView] = useArchiveView();
  return (
    <>
      <Tabs
        label={`Active or archived ${KINDS[kind].plural}`}
        value={view}
        onChange={setView}
        items={[
          { key: "active", label: "Active", count: active },
          { key: "archived", label: "Archived", count: archived },
        ]}
      />
      {view === "archived" && Boolean(archived) && (
        <p className="rounded-[9px] bg-subtle px-4 py-3 text-[13px] leading-normal text-body">
          {KINDS[kind].archivedNote}
        </p>
      )}
    </>
  );
}

export function ArchivedBadge() {
  return <Pill className="shrink-0">Archived</Pill>;
}

/** "Archived 12 Oct by Asha Rao. Read-only." with Restore, or who may restore it. */
export function ArchivedBanner({
  kind,
  row,
  mayRestore,
  onRestore,
  children,
}: {
  kind: ArchiveKind;
  row: Archivable;
  mayRestore: boolean;
  onRestore: () => void;
  /** Extra line, e.g. "Still used by 7 past experiments." */
  children?: ReactNode;
}) {
  if (!row.archived_at) return null;
  const by = row.archived_by ? ` by ${row.archived_by}` : "";
  return (
    <section
      aria-label="Archived"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-[9px] bg-subtle px-4 py-3 text-[13px] leading-normal text-body"
    >
      <p className="min-w-0 flex-1">
        <span className="font-medium text-ink">
          Archived {formatDate(row.archived_at)}
          {by}.
        </span>{" "}
        Read-only. {children}
        {!mayRestore && (
          <span className="block text-muted">
            {KINDS[kind].whoRestores}
            {row.archived_by ? `. Ask ${row.archived_by}.` : "."}
          </span>
        )}
      </p>
      {mayRestore && (
        <Button variant="primary" onClick={onRestore}>
          Restore
        </Button>
      )}
    </section>
  );
}

/** The Active tab with nothing in it, or the Archived one. */
export function ArchiveEmpty({
  kind,
  view,
  archived,
  create,
}: {
  kind: ArchiveKind;
  view: ArchiveView;
  archived: number;
  /** The list's own "New …" button, for people who may make one. */
  create?: ReactNode;
}) {
  const [, setView] = useArchiveView();
  const { noun, plural: many } = KINDS[kind];
  if (view === "archived")
    return (
      <EmptyState
        title="Nothing archived"
        action={<Button onClick={() => setView("active")}>Back to active</Button>}
      >
        Archive a {noun} you no longer use to tidy this list. Past work keeps it.
      </EmptyState>
    );
  return (
    <EmptyState
      title={`No active ${many}`}
      action={
        <div className="flex flex-wrap justify-center gap-2">
          {create}
          <Button onClick={() => setView("archived")}>See archived ({archived})</Button>
        </div>
      }
    >
      {archived === 1 ? "1 is" : `${archived} are`} archived. Make a new one, or restore an old one.
    </EmptyState>
  );
}

/**
 * Under a search or a picker that found nothing active: "1 archived dataset
 * also matches. See archived", which opens that list's Archived tab. With no
 * search (a picker without one): "2 archived evaluators aren't shown."
 */
export function ArchivedMatches({ kind, q = "" }: { kind: ArchiveKind; q?: string | undefined }) {
  const archived = useArchivedList<{ name: string }>(kind);
  const word = q.trim().toLowerCase();
  const count = (archived.data ?? []).filter((r) => r.name.toLowerCase().includes(word)).length;
  if (count === 0) return null;
  const { page, noun } = KINDS[kind];
  const search = word ? `&q=${encodeURIComponent(q.trim())}` : "";
  const to = `${page}${page.includes("?") ? "&" : "?"}view=archived${search}`;
  const what = plural(count, `archived ${noun}`);
  return (
    <span className="text-xs text-muted">
      {word
        ? `${what} also ${count === 1 ? "matches" : "match"}.`
        : `${what} ${count === 1 ? "isn't" : "aren't"} shown.`}{" "}
      <Link className="link" to={to}>
        See archived
      </Link>
    </span>
  );
}
